import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import SearchSelect from "@/components/SearchSelect";

/**
 * Dependent FilterBar — all four filters narrow each other in real time.
 *
 * Whenever one value changes the other three ref lists are re-fetched with the
 * current filters passed as query params so the user can only see valid
 * combinations (e.g. selecting Cartier narrows the location list to only
 * showrooms that actually sell Cartier). All four pickers use the searchable
 * `SearchSelect` so users can type to narrow long lists (200+ salesmen, etc).
 */
const FilterBar = ({ value, onChange, showSalesman = true }) => {
  const [brands, setBrands] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [categories, setCategories] = useState([]);

  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    const ctrl = new AbortController();
    const params = (exclude) => {
      const qp = new URLSearchParams();
      if (exclude !== "company" && value.company) qp.append("company", value.company);
      if (exclude !== "brand" && value.brand) qp.append("brand", value.brand);
      if (exclude !== "location_code" && value.location_code) qp.append("location_code", value.location_code);
      if (exclude !== "salesman_code" && value.salesman_code) qp.append("salesman_code", value.salesman_code);
      if (exclude !== "category" && value.category) qp.append("category", value.category);
      const s = qp.toString();
      return s ? `?${s}` : "";
    };
    api.get(`/ref/brands${params("brand")}`, { signal: ctrl.signal }).then((r) => setBrands(r.data.rows || [])).catch(() => {});
    api.get(`/ref/companies${params("company")}`, { signal: ctrl.signal }).then((r) => setCompanies(r.data.rows || [])).catch(() => {});
    api.get(`/ref/locations${params("location_code")}`, { signal: ctrl.signal }).then((r) => setLocations(r.data.rows || [])).catch(() => {});
    api.get(`/ref/categories${params("category")}`, { signal: ctrl.signal }).then((r) => setCategories(r.data.rows || [])).catch(() => {});
    if (showSalesman) {
      api.get(`/ref/salesmen${params("salesman_code")}`, { signal: ctrl.signal }).then((r) => setSalesmen(r.data.rows || [])).catch(() => {});
    }
    return () => ctrl.abort();
  }, [value.brand, value.company, value.location_code, value.salesman_code, value.category, showSalesman]);

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-3 mb-5 no-print">
      <SearchSelect
        testId="filter-company"
        width="w-[170px]"
        placeholder="Company"
        allLabel="All companies"
        value={value.company || ""}
        onChange={(v) => set({ company: v })}
        options={companies.map((c) => ({ value: String(c.code), label: c.name, sub: c.code }))}
      />
      <SearchSelect
        testId="filter-location"
        width="w-[220px]"
        placeholder="Location"
        allLabel="All locations"
        value={value.location_code || ""}
        onChange={(v) => set({ location_code: v })}
        options={locations.map((l) => ({ value: String(l.code), label: l.name || l.code, sub: l.code }))}
      />
      <SearchSelect
        testId="filter-brand"
        width="w-[180px]"
        placeholder="Brand"
        allLabel="All brands"
        value={value.brand || ""}
        onChange={(v) => set({ brand: v })}
        options={brands.map((b) => ({ value: b.name, label: b.name }))}
      />
      <SearchSelect
        testId="filter-category"
        width="w-[180px]"
        placeholder="Category"
        allLabel="All categories"
        value={value.category || ""}
        onChange={(v) => set({ category: v })}
        options={categories.map((c) => ({ value: c.name, label: c.name }))}
      />
      {showSalesman && (
        <SearchSelect
          testId="filter-salesman"
          width="w-[200px]"
          placeholder="Salesman"
          allLabel="All salesmen"
          value={value.salesman_code || ""}
          onChange={(v) => set({ salesman_code: v })}
          options={salesmen.map((s) => ({ value: String(s.code), label: s.name || s.code, sub: s.code }))}
        />
      )}

      <input
        data-testid="filter-start"
        type="date"
        value={value.start_date || ""}
        onChange={(e) => set({ start_date: e.target.value, preset: "" })}
        className="bg-background border border-border rounded-sm px-3 py-2 text-sm"
      />
      <input
        data-testid="filter-end"
        type="date"
        value={value.end_date || ""}
        onChange={(e) => set({ end_date: e.target.value, preset: "" })}
        className="bg-background border border-border rounded-sm px-3 py-2 text-sm"
      />

      {(value.brand || value.company || value.location_code || value.salesman_code || value.category || value.start_date || value.end_date) && (
        <button
          data-testid="filter-reset"
          onClick={() => onChange({ preset: value.preset || "ytd" })}
          className="text-[11px] uppercase tracking-wider px-3 py-2 border border-myb-200 text-muted-foreground hover:text-myb-500 hover:border-myb-500 rounded-sm font-semibold"
        >
          Reset filters
        </button>
      )}
    </div>
  );
};

export default FilterBar;
